import { cn } from '@dispatch-ui/utils';
import { useState } from 'react';
import { useTranslation } from '../../hooks/use-translation';

// Mirrors QuizVersion::HOME_DEPOT_SKILL_KEY on the backend.
export const HOME_DEPOT_SKILL_KEY = 'home_depot_certified';

type CalloutProps = {
  variant?: 'orange' | 'blue';
  children: React.ReactNode;
};

function Callout({ variant = 'orange', children }: CalloutProps) {
  return (
    <div
      className={cn(
        'my-3.5 rounded-lg border-l-4 p-3 text-sm',
        variant === 'orange'
          ? 'border-orange bg-orange-100'
          : 'border-blue bg-blue-100',
      )}
    >
      {children}
    </div>
  );
}

function PriorityBanner({ title, sub }: { title: string; sub: string }) {
  const lightning = '⚡';

  return (
    <div className="mb-4 flex items-center gap-3.5 rounded-xl bg-navy p-4 text-white">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-orange text-xl">
        {lightning}
      </div>
      <div>
        <div className="text-sm font-bold">{title}</div>
        <div className="mt-0.5 text-xs text-blue-200">{sub}</div>
      </div>
    </div>
  );
}

function RiskBanner({ title, sub }: { title: string; sub: string }) {
  const exclamation = '!';

  return (
    <div className="my-4 flex items-start gap-3.5 rounded-xl border border-red-100 bg-red-50 p-4">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-500 text-lg font-bold text-white">
        {exclamation}
      </div>
      <div>
        <div className="text-sm font-bold text-red-700">{title}</div>
        <div className="mt-0.5 text-xs text-red-600">{sub}</div>
      </div>
    </div>
  );
}

type DoDontProps = {
  doTitle: string;
  doItems: string[];
  doVariant?: 'blue' | 'orange';
  dontTitle: string;
  dontItems: string[];
};

function DoDont({
  doTitle,
  doItems,
  doVariant = 'blue',
  dontTitle,
  dontItems,
}: DoDontProps) {
  return (
    <div className="my-3 grid gap-3.5 sm:grid-cols-2">
      <div
        className={cn(
          'rounded-lg p-4 text-sm',
          doVariant === 'orange' ? 'bg-orange-100' : 'bg-blue-100',
        )}
      >
        <div
          className={cn(
            'mb-1.5 text-xs font-bold',
            doVariant === 'orange' ? 'text-orange-700' : 'text-navy',
          )}
        >
          {doTitle}
        </div>
        <ul className="list-disc space-y-1 pl-4 text-xs">
          {doItems.map(item => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
      <div className="rounded-lg bg-red-50 p-4 text-sm">
        <div className="mb-1.5 text-xs font-bold text-red-700">{dontTitle}</div>
        <ul className="list-disc space-y-1 pl-4 text-xs">
          {dontItems.map(item => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

type TitledParagraphProps = {
  strong: string;
  rest: string;
};

function TitledText({ strong, rest }: TitledParagraphProps) {
  return (
    <>
      <strong className="text-orange-800">{strong}</strong> {rest}
    </>
  );
}

function NavyTitledText({ strong, rest }: TitledParagraphProps) {
  return (
    <>
      <strong className="text-navy">{strong}</strong> {rest}
    </>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={cn(
        'h-5 w-5 flex-shrink-0 text-blue-700 transition-transform',
        open && 'rotate-180',
      )}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function numberBadgeClasses(isDone: boolean, isOpen: boolean): string {
  if (isDone) return 'bg-navy text-white';
  if (isOpen) return 'bg-blue text-white';
  return 'bg-blue-100 text-navy';
}

type GuideSection = {
  eyebrow: string;
  title: string;
  body: React.ReactNode;
};

type HdGuideProps = {
  onUnlock: () => void;
};

function HdGuide({ onUnlock }: HdGuideProps): JSX.Element {
  const { t } = useTranslation('quiz');
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [readIndexes, setReadIndexes] = useState<Set<number>>(new Set());

  const toggle = (index: number) => {
    const isOpen = openIndex === index;
    setOpenIndex(isOpen ? null : index);
    if (!isOpen) {
      setReadIndexes(previous => new Set(previous).add(index));
    }
  };

  const sections: GuideSection[] = [
    {
      eyebrow: t('GUIDE_S1_EYEBROW'),
      title: t('GUIDE_S1_TITLE'),
      body: (
        <>
          <h3 className="mb-2 mt-0 text-base font-bold text-blue-700">
            {t('GUIDE_S1_HEADING')}
          </h3>
          <ul className="list-disc space-y-1.5 pl-5 text-sm">
            {[
              t('GUIDE_S1_ITEM_1'),
              t('GUIDE_S1_ITEM_2'),
              t('GUIDE_S1_ITEM_3'),
            ].map(item => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <Callout>
            <TitledText
              strong={t('GUIDE_S1_CALLOUT_1_STRONG')}
              rest={t('GUIDE_S1_CALLOUT_1_REST')}
            />
          </Callout>
          <table className="my-3 w-full text-sm">
            <tbody>
              {(
                [
                  [t('GUIDE_S1_VEHICLE_CAR'), t('GUIDE_S1_VEHICLE_CAR_RADIUS')],
                  [t('GUIDE_S1_VEHICLE_VAN'), t('GUIDE_S1_VEHICLE_VAN_RADIUS')],
                ] as [string, string][]
              ).map(([vehicle, radius]) => (
                <tr key={vehicle} className="border-b border-blue-100">
                  <td className="w-28 whitespace-nowrap py-2 pr-3 font-bold text-navy">
                    {vehicle}
                  </td>
                  <td className="py-2">{radius}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="-mt-1.5 text-xs text-gray-500">{t('GUIDE_S1_NOTE')}</p>
          <Callout variant="blue">
            <NavyTitledText
              strong={t('GUIDE_S1_CALLOUT_2_STRONG')}
              rest={t('GUIDE_S1_CALLOUT_2_REST')}
            />
          </Callout>
        </>
      ),
    },
    {
      eyebrow: t('GUIDE_S2_EYEBROW'),
      title: t('GUIDE_S2_TITLE'),
      body: (
        <>
          <PriorityBanner
            title={t('GUIDE_S2_PRIORITY_BANNER_TITLE')}
            sub={t('GUIDE_S2_PRIORITY_BANNER_SUB')}
          />
          <h3 className="mb-2 mt-0 text-base font-bold text-blue-700">
            {t('GUIDE_S2_HEADING')}
          </h3>
          <DoDont
            doTitle={t('GUIDE_S2_DO_TITLE')}
            doItems={[
              t('GUIDE_S2_DO_ITEM_1'),
              t('GUIDE_S2_DO_ITEM_2'),
              t('GUIDE_S2_DO_ITEM_3'),
              t('GUIDE_S2_DO_ITEM_4'),
            ]}
            dontTitle={t('GUIDE_S2_DONT_TITLE')}
            dontItems={[
              t('GUIDE_S2_DONT_ITEM_1'),
              t('GUIDE_S2_DONT_ITEM_2'),
              t('GUIDE_S2_DONT_ITEM_3'),
              t('GUIDE_S2_DONT_ITEM_4'),
            ]}
          />
        </>
      ),
    },
    {
      eyebrow: t('GUIDE_S3_EYEBROW'),
      title: t('GUIDE_S3_TITLE'),
      body: (
        <>
          <h3 className="mb-2 mt-0 text-base font-bold text-blue-700">
            {t('GUIDE_S3_HEADING')}
          </h3>
          <ul className="list-disc space-y-1.5 pl-5 text-sm">
            {[t('GUIDE_S3_ITEM_1'), t('GUIDE_S3_ITEM_2')].map(item => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <Callout>
            <TitledText
              strong={t('GUIDE_S3_CALLOUT_STRONG')}
              rest={t('GUIDE_S3_CALLOUT_REST')}
            />
          </Callout>
        </>
      ),
    },
    {
      eyebrow: t('GUIDE_S4_EYEBROW'),
      title: t('GUIDE_S4_TITLE'),
      body: (
        <>
          <h3 className="mb-2 mt-0 text-base font-bold text-blue-700">
            {t('GUIDE_S4_HEADING')}
          </h3>
          <Callout>
            <TitledText
              strong={t('GUIDE_S4_CALLOUT_1_STRONG')}
              rest={t('GUIDE_S4_CALLOUT_1_REST')}
            />
          </Callout>
          <Callout>
            <TitledText
              strong={t('GUIDE_S4_CALLOUT_2_STRONG')}
              rest={t('GUIDE_S4_CALLOUT_2_REST')}
            />
          </Callout>
          <DoDont
            doTitle={t('GUIDE_S4_DO_TITLE')}
            doItems={[
              t('GUIDE_S4_DO_ITEM_1'),
              t('GUIDE_S4_DO_ITEM_2'),
              t('GUIDE_S4_DO_ITEM_3'),
              t('GUIDE_S4_DO_ITEM_4'),
              t('GUIDE_S4_DO_ITEM_5'),
              t('GUIDE_S4_DO_ITEM_7'),
              t('GUIDE_S4_DO_ITEM_6'),
            ]}
            dontTitle={t('GUIDE_S4_DONT_TITLE')}
            dontItems={[
              t('GUIDE_S4_DONT_ITEM_1'),
              t('GUIDE_S4_DONT_ITEM_2'),
              t('GUIDE_S4_DONT_ITEM_3'),
              t('GUIDE_S4_DONT_ITEM_4'),
              t('GUIDE_S4_DONT_ITEM_5'),
            ]}
          />
          <h3 className="mb-2 mt-4 text-base font-bold text-blue-700">
            {t('GUIDE_S4_SUBHEADING')}
          </h3>
          <ul className="list-disc space-y-1.5 pl-5 text-sm">
            <li>
              <TitledText
                strong={t('GUIDE_S4_LI_1_STRONG')}
                rest={t('GUIDE_S4_LI_1_REST')}
              />
            </li>
            <li>
              <TitledText
                strong={t('GUIDE_S4_LI_2_STRONG')}
                rest={t('GUIDE_S4_LI_2_REST')}
              />
            </li>
          </ul>
          <RiskBanner
            title={t('GUIDE_S4_RISK_TITLE')}
            sub={t('GUIDE_S4_RISK_SUB')}
          />
          <RiskBanner
            title={t('GUIDE_S4_RISK_2_TITLE')}
            sub={t('GUIDE_S4_RISK_2_SUB')}
          />
        </>
      ),
    },
  ];

  const totalSections = sections.length;
  const readCount = readIndexes.size;
  const allRead = readCount >= totalSections;

  return (
    <div className="w-full max-w-3xl">
      <div className="mb-5 rounded-2xl border border-blue-100 bg-white p-7">
        <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-orange-800">
          {t('GUIDE_EYEBROW')}
        </div>
        <h2 className="mb-3.5 text-xl font-bold text-navy">
          {t('GUIDE_HEADING')}
        </h2>
        <p className="text-sm">
          {t('GUIDE_INTRO_BEFORE')} <strong>{t('GUIDE_INTRO_STRONG')}</strong>
          {t('GUIDE_INTRO_AFTER')}
        </p>
        <div className="mt-3.5 flex items-center gap-2.5 text-xs font-bold text-navy">
          <span>
            {t('GUIDE_PROGRESS_LABEL', {
              replace: {
                read: String(readCount),
                total: String(totalSections),
              },
            })}
          </span>
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-blue-100">
            <div
              className="h-full rounded-full bg-orange transition-all"
              style={{ width: `${(readCount / totalSections) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="mb-5 flex flex-col gap-3">
        {sections.map((section, i) => {
          const index = i + 1;
          const isOpen = openIndex === index;
          const isDone = readIndexes.has(index);

          return (
            <div
              key={section.title}
              className={cn(
                'overflow-hidden rounded-2xl border bg-white',
                isOpen ? 'border-blue' : 'border-blue-100',
              )}
            >
              <button
                type="button"
                onClick={() => toggle(index)}
                aria-expanded={isOpen}
                className="flex w-full items-center gap-4 p-5 text-left"
              >
                <div
                  className={cn(
                    'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold transition-colors',
                    numberBadgeClasses(isDone, isOpen),
                  )}
                >
                  {String(index).padStart(2, '0')}
                </div>
                <div className="flex-1">
                  <div className="mb-0.5 text-[11px] font-bold uppercase tracking-wide text-orange-800">
                    {section.eyebrow}
                  </div>
                  <div className="text-base font-bold text-navy">
                    {section.title}
                  </div>
                </div>
                <div
                  aria-hidden="true"
                  className={cn(
                    'relative h-[22px] w-[22px] flex-shrink-0 rounded-full border-2',
                    isDone
                      ? 'border-green-500 bg-green-500'
                      : 'border-blue-100',
                  )}
                >
                  {isDone && (
                    <span className="absolute left-[6px] top-[2px] h-[10px] w-[5px] rotate-45 border-b-2 border-r-2 border-white" />
                  )}
                </div>
                <ChevronIcon open={isOpen} />
              </button>
              {isOpen && (
                <div className="mx-5 border-t border-blue-100 px-0 pb-6 pt-4">
                  {section.body}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={!allRead}
          onClick={onUnlock}
          className={cn(
            'rounded-lg px-6 py-3 text-sm font-bold transition-opacity',
            allRead
              ? 'bg-blue text-white hover:opacity-90'
              : 'cursor-not-allowed bg-blue-100 text-navy opacity-45',
          )}
        >
          {allRead
            ? t('GUIDE_UNLOCKED_BUTTON')
            : t('GUIDE_LOCKED_BUTTON', {
                replace: { total: String(totalSections) },
              })}
        </button>
      </div>
    </div>
  );
}

export default HdGuide;
